import React, {useEffect, useMemo, useState} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  StatusBar,
  Image,
  TouchableOpacity,
  Alert,
} from 'react-native';
import Clipboard from '@react-native-clipboard/clipboard';
import SimpleHeader from '../../components/SimpleHeader';
import TabBar from '../../components/TabBar';
import {COLORS} from '../../theme/themeTokens';
import type {V2HistoryRecord, V2TransactionType} from '../../types/v2';
import {v2GetTransactionTypes} from '../../services/v2/api';
import {formatSignedAmount, signedAmountStyle} from '../../utils/transactionSign';
import {numberToAmountDisplay} from '../../utils/amountFormat';

// ── Helpers ──────────────────────────────────────────────────────────────────

const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const formatDate = (dateString: string): { date: string; time: string } => {
  const d = new Date(dateString);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  const hours = d.getHours().toString().padStart(2, '0');
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const seconds = d.getSeconds().toString().padStart(2, '0');
  return {
    date: `${day}.${month}.${year}`,
    time: `${hours}:${minutes}:${seconds}`,
  };
};

const getStatusColor = (status: string): {bg: string; text: string} => {
  const lower = status.toLowerCase();
  // BC-shim: matches both legacy (`completed`/`processing`/`failed`) and new
  // vocab (`confirmed`/`in_process`/`cancelled`) — нужно на переходный
  // период между деплоями backend 1.3 → 1.4. TODO: убрать старые имена
  // после полного деплоя backend 1.4 на прод (см. CLAUDE.md / migration 00053).
  if (lower === 'completed' || lower === 'success' || lower === 'confirmed')
    return {bg: '#E6F4EA', text: '#0D803B'};
  if (lower === 'pending' || lower === 'processing' || lower === 'in_process')
    return {bg: '#FFF3E0', text: '#E65100'};
  if (lower === 'failed' || lower === 'rejected' || lower === 'cancelled')
    return {bg: '#FDECEA', text: '#C62828'};
  return {bg: COLORS.softGrey, text: COLORS.slate};
};

// ── Props ────────────────────────────────────────────────────────────────────

interface Props {
  record: V2HistoryRecord;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  /** Opens the request this operation came from. */
  onOpenRequest?: (requestID: string) => void;
}

// ── Component ────────────────────────────────────────────────────────────────

const TransactionHistoryDetailScreen: React.FC<Props> = ({record, onBack, onNavigate, onOpenRequest}) => {
  // Pull the catalog so we can resolve the sign visual the same way list
  // screens do (formatSignedAmount + signedAmountStyle). Falls back to a
  // null sign while loading or on error → renders the raw amount with
  // default text color, never the wrong sign.
  const [transactionTypes, setTransactionTypes] = useState<V2TransactionType[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await v2GetTransactionTypes();
        if (!cancelled) {
          setTransactionTypes(res.transaction_types ?? []);
        }
      } catch (err) {
        console.warn('[TransactionHistoryDetailScreen] Failed to fetch transaction types:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const amountSign = useMemo<V2TransactionType['amount_sign'] | null>(() => {
    const match = transactionTypes.find(t => t.name === record.transaction_type_name);
    return match?.amount_sign ?? null;
  }, [transactionTypes, record.transaction_type_name]);

  const formattedAmount = formatSignedAmount(record.amount, amountSign);
  const {color: amountColor} = signedAmountStyle(amountSign);
  const statusColor = getStatusColor(record.transaction_status_name);
  // BC-shim: matches both legacy `completed` и новый `confirmed` (см. getStatusColor).
  const isCompleted =
    record.transaction_status_name.toLowerCase() === 'completed' ||
    record.transaction_status_name.toLowerCase() === 'success' ||
    record.transaction_status_name.toLowerCase() === 'confirmed';

  // Blockchain section is now data-driven — if backend included on-chain
  // metadata on the record, render it. Previously gated on a string match
  // for "withdr", which would silently hide the block when admins renamed
  // the withdraw type and would miss any future on-chain inflow that
  // ships these fields.
  // `network_name` is the derived chain name from the backend overlay
  // (account.source_balance → token.network) — populated for deposit-
  // flow records even though their `network` column is NULL. We
  // include it in the gate so the blockchain block opens for those
  // records and the Network row can render.
  const networkLabel = record.network || record.network_name || null;
  const hasBlockchainInfo = Boolean(
    record.wallet_address || networkLabel || record.destination_address || record.transaction_hash,
  );

  // Copy to clipboard helper
  const copyToClipboard = (text: string, fieldName: string) => {
    Clipboard.setString(text);
    Alert.alert('Copied', `${fieldName} copied to clipboard`);
  };

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={COLORS.white} translucent={false} />

      <SimpleHeader
        title="Transaction details"
        onBack={onBack}
        backgroundVariant="white"
      />

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}>
        {/* Icon */}
        <View style={styles.iconContainer}>
          <Image
            source={require('../../assets/wallet2.png')}
            style={styles.tokenIcon}
            resizeMode="contain"
          />
        </View>

        {/* Type */}
        <Text style={styles.typeName}>
          {capitalize(record.transaction_type_name)}
        </Text>

        {/* Amount + currency */}
        <Text
          style={[
            styles.amount,
            {color: amountColor},
          ]}>
          {formattedAmount} {record.token_symbol || record.currency}
        </Text>

        {/* Detail card */}
        <View style={styles.card}>
          {/* Status */}
          <View style={styles.row}>
            <Text style={styles.label}>Status</Text>
            <View style={styles.statusValueContainer}>
              {isCompleted && (
                <Image
                  source={require('../../assets/check.png')}
                  style={[styles.statusIcon, {tintColor: statusColor.text}]}
                  resizeMode="contain"
                />
              )}
              {record.transaction_status_name.toLowerCase() === 'pending' && (
                <Image
                  source={require('../../assets/clock-blue-steel.png')}
                  style={[styles.statusIcon, {tintColor: COLORS.blueSteel}]}
                  resizeMode="contain"
                />
              )}
              <Text style={[
                styles.statusValue,
                record.transaction_status_name.toLowerCase() === 'pending' 
                  ? {color: COLORS.blueSteel} 
                  : {color: statusColor.text}
              ]}>
                {capitalize(record.transaction_status_name)}
              </Text>
            </View>
          </View>

          {/* The request behind the operation — tapping it opens the order. */}
          {record.source_request_number && record.source_request_id ? (
            <View style={styles.row}>
              <Text style={styles.label}>Request</Text>
              <TouchableOpacity
                onPress={() => onOpenRequest?.(record.source_request_id!)}
                disabled={!onOpenRequest}
                accessibilityRole="link"
              >
                <Text style={[styles.value, onOpenRequest && styles.linkValue]}>
                  {`№${record.source_request_number}`}
                </Text>
              </TouchableOpacity>
            </View>
          ) : null}

          {/* Transaction type */}
          <View style={styles.row}>
            <Text style={styles.label}>Transaction type</Text>
            <Text style={styles.value}>
              {capitalize(record.transaction_type_name)}
            </Text>
          </View>

          {/* Amount */}
          <View style={styles.row}>
            <Text style={styles.label}>Amount</Text>
            <Text style={styles.value}>
              {formattedAmount} <Text style={styles.currencyText}>{record.token_symbol || record.currency}</Text>
            </Text>
          </View>

          {/* Rate — what an exchange actually traded at. */}
          {record.conversion_rate && record.conversion_base && record.conversion_quote ? (
            <View style={styles.row}>
              <Text style={styles.label}>Rate</Text>
              <Text style={styles.value}>
                {`1 ${record.conversion_base} = ${numberToAmountDisplay(record.conversion_rate, 8)} `}
                <Text style={styles.currencyText}>{record.conversion_quote}</Text>
              </Text>
            </View>
          ) : null}

          {/* Date */}
          <View style={styles.row}>
            <Text style={styles.label}>Date</Text>
            <Text style={styles.value}>
              {(() => {
                const { date, time } = formatDate(record.date);
                return (
                  <>
                    {date} <Text style={styles.timeText}>{time}</Text>
                  </>
                );
              })()}
            </Text>
          </View>

          {/* Blockchain info for withdraw */}
          {hasBlockchainInfo && (
            <>
              {/* Wallet Address */}
              {record.wallet_address && (
                <View style={styles.row}>
                  <Text style={styles.label}>Wallet address</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value} numberOfLines={2}>
                      {record.wallet_address}
                    </Text>
                    <TouchableOpacity
                      style={styles.copyIconBtn}
                      activeOpacity={0.7}
                      onPress={() => copyToClipboard(record.wallet_address!, 'Wallet address')}
                    >
                      <Image
                        source={require('../../assets/copy.png')}
                        style={styles.copyIcon}
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              )}

              {/* Network — `record.network` for on-chain records,
                  `record.network_name` (derived from source_balance
                  overlay) for deposit-flow records. */}
              {networkLabel && (
                <View style={styles.row}>
                  <Text style={styles.label}>Network</Text>
                  <Text style={styles.value}>{networkLabel}</Text>
                </View>
              )}

              {/* Destination Address */}
              {record.destination_address && (
                <View style={styles.row}>
                  <Text style={styles.label}>Destination</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value} numberOfLines={2}>
                      {record.destination_address}
                    </Text>
                    <TouchableOpacity
                      style={styles.copyIconBtn}
                      activeOpacity={0.7}
                      onPress={() => copyToClipboard(record.destination_address!, 'Destination address')}
                    >
                      <Image
                        source={require('../../assets/copy.png')}
                        style={styles.copyIcon}
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              )}

              {/* Transaction Hash */}
              {record.transaction_hash && (
                <View style={styles.row}>
                  <Text style={styles.label}>Transaction hash</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value} numberOfLines={2}>
                      {record.transaction_hash}
                    </Text>
                    <TouchableOpacity
                      style={styles.copyIconBtn}
                      activeOpacity={0.7}
                      onPress={() => copyToClipboard(record.transaction_hash!, 'Transaction hash')}
                    >
                      <Image
                        source={require('../../assets/copy.png')}
                        style={styles.copyIcon}
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              )}
            </>
          )}
        </View>
      </ScrollView>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />
    </View>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.white,
  },
  content: {
    flex: 1,
    backgroundColor: COLORS.white,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
  },
  scrollContent: {
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 120,
  },

  // Icon
  iconContainer: {
    marginTop: 12,
    marginBottom: 6,
    width: 72,
    height: 72,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tokenIcon: {
    width: 72,
    height: 72,
    borderRadius: 36,
  },
  tokenIconPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: COLORS.softGrey,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tokenIconLetter: {
    fontSize: 20,
    fontWeight: '600',
    color: COLORS.steelNavy,
  },

  // Type + amount header
  typeName: {
    fontFamily: 'Inter',
    fontSize: 18,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.slate,
    marginTop: 4,
    marginBottom: 2,
  },
  amount: {
    fontFamily: 'Helvetica Neue',
    fontSize: 32,
    fontWeight: '500',
    lineHeight: 48,
    marginBottom: 12,
  },

  // Detail card
  card: {
    width: '100%',
    backgroundColor: COLORS.pureLight,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 12,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: 40,
    paddingVertical: 8,
  },
  rowWithFrame: {
    paddingVertical: 0,
  },
  label: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.blueSteel,
  },
  linkValue: {
    color: COLORS.brandOrange,
    textDecorationLine: 'underline',
  },
  value: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.deepNavy,
    textAlign: 'right',
    flexShrink: 1,
    marginLeft: 16,
  },
  innerFrame: {
    flexDirection: 'column',
    backgroundColor: COLORS.softGrey,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 16,
    marginBottom: 4,
    flex: 1,
  },
  innerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 2,
  },
  labelInner: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.blueSteel,
  },
  valueInner: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.deepNavy,
    textAlign: 'right',
  },
  currencyText: {
    color: COLORS.blueSteel,
  },
  timeText: {
    color: COLORS.blueSteel,
  },

  // Status value
  statusValueContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  statusIcon: {
    width: 14,
    height: 14,
  },
  statusValue: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
  },

  // Value row with copy icon
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    justifyContent: 'flex-end',
  },
  copyIconBtn: {
    padding: 4,
    marginLeft: 4,
  },
  copyIcon: {
    width: 16,
    height: 16,
    tintColor: COLORS.blueSteel,
  },
});

export default TransactionHistoryDetailScreen;
